import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, mergeProps as _mergeProps, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "grid min-h-full grid-cols-[auto_1fr] bg-white" };
const _hoisted_2 = { class: "flex min-w-0 flex-col gap-6 bg-white px-5 pt-6 pb-4" };
const _hoisted_3 = { class: "flex flex-col gap-2" };
const _hoisted_4 = { class: "text-2xl font-bold text-(--colors-text-heading) sm:text-3xl" };
const _hoisted_5 = { class: "text-sm text-(--colors-text-heading)" };
import { computed, h, onMounted, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { Badge, Breadcrumb, DataTable, Pager, TableFilterSelector, TableShell, useColumnSettings, useTableQuery, useTableViews, } from "@eudonet/opal";
import { moduleRegistry } from "@/router/moduleRegistry";
import ModuleState from "@/shared/components/ModuleState.vue";
import { buildColumnSettingsLabels } from "@/shared/composables/columnSettingsLabels";
import { buildPagerLabels } from "@/shared/composables/pagerLabels";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { listsInTab, matchesListSearch, VALUE_LIST_TABS, valueListName, } from "../../domain/valueList";
import { identityLanguage, secondaryLanguages } from "../../domain/valueListValue";
import { useValueListStore } from "../../stores/useValueListStore";
import { useValueListTable } from "../composables/useValueListTable";
const LANGUAGE_COLUMN_PREFIX = "name";
const ROW_LINK_CLASS = "rounded-xs text-(--colors-text-link) hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-(--colors-text-link)";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ValueListManagerView',
    setup(__props) {
        const ROW_OPTIONS = [10, 30, 50];
        const { t, locale } = useI18n();
        const store = useValueListStore();
        const { secondaryLanguageHeaders, toSettingsColumns, tabDropdowns, filterSelectorLabels, inertFilterFields } = useValueListTable();
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0], activeViewId: "all" });
        const tableLabels = computed(() => buildTableLabels(t));
        const pagerLabels = computed(() => buildPagerLabels(t));
        const breadcrumb = computed(() => [{ label: t("settings.title") }, { label: t("valueLists.title") }]);
        const TAB_LABEL_KEYS = {
            all: "valueLists.tabs.all",
            referentials: "valueLists.tabs.referentials",
            contactOrganisation: "valueLists.tabs.contactOrganisation",
            finance: "valueLists.tabs.finance",
        };
        const tableViews = useTableViews({ initialViews: [] });
        const viewTabs = computed(() => VALUE_LIST_TABS.map((tab) => ({
            id: tab,
            label: t(TAB_LABEL_KEYS[tab]),
            count: listsInTab(store.lists, tab).length,
            isPreset: true,
            isDefault: tab === "all",
            querySnapshot: {},
        })));
        watch(viewTabs, (next) => (tableViews.views.value = next), { immediate: true });
        const activeTab = computed(() => {
            const id = tableQuery.state.activeViewId;
            return VALUE_LIST_TABS.find((tab) => tab === id) ?? "all";
        });
        const filterFields = computed(() => inertFilterFields([
            { key: "family", label: t("valueLists.filter.family"), icon: "ri-folder-2-line" },
            { key: "code", label: t("valueLists.columns.code"), icon: "ri-hashtag" },
            { key: "activeValues", label: t("valueLists.columns.activeValues"), icon: "ri-checkbox-circle-line" },
        ]));
        const searchTerm = computed(() => tableQuery.state.search ?? "");
        const filteredRows = computed(() => listsInTab(store.lists, activeTab.value).filter((list) => matchesListSearch(list, searchTerm.value)));
        const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / tableQuery.state.pageSize)));
        const pageRows = computed(() => {
            const start = (tableQuery.state.page - 1) * tableQuery.state.pageSize;
            return filteredRows.value.slice(start, start + tableQuery.state.pageSize);
        });
        // ── Columns ──────────────────────────────────────────────────────────────────
        const languageColumnId = (language) => `${LANGUAGE_COLUMN_PREFIX}-${language}`;
        const columnHeaders = computed(() => ({
            name: t("valueLists.columns.identity", { code: identityLanguage(locale.value).toUpperCase() }),
            ...secondaryLanguageHeaders(LANGUAGE_COLUMN_PREFIX),
            code: t("valueLists.columns.code"),
            activeValues: t("valueLists.columns.activeValues"),
        }));
        const columnSettings = useColumnSettings({
            columns: () => toSettingsColumns(columnHeaders.value, "name"),
            defaultPinning: { left: ["name"], right: [] },
            labels: () => buildColumnSettingsLabels(t),
        });
        const columns = computed(() => [
            {
                id: "name",
                header: columnHeaders.value.name,
                cell: ({ row }) => h(RouterLink, {
                    to: { name: "ValueListDetail", params: { code: row.original.code } },
                    class: ROW_LINK_CLASS,
                    "data-testid": `value-list-row-link-${row.original.code}`,
                }, () => valueListName(row.original, locale.value)),
            },
            ...secondaryLanguages(locale.value).map((language) => ({
                id: languageColumnId(language),
                header: columnHeaders.value[languageColumnId(language)],
                cell: ({ row }) => row.original.names[language],
            })),
            {
                id: "code",
                header: columnHeaders.value.code,
                cell: ({ row }) => h(Badge, { label: row.original.code, textOnly: true }),
            },
            {
                id: "activeValues",
                header: columnHeaders.value.activeValues,
                // CONSTRAINT: OPCAT's catalog listing carries no value counts yet; render "n / total" here
                // only once it does, counted with the same rule as the detail screen's "displayed" tab.
                cell: () => h("span", { class: "text-(--colors-text-body-subtle)" }, "–"),
            },
        ]);
        onMounted(() => store.ensureLoaded());
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("main", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(Breadcrumb), {
                            items: breadcrumb.value,
                            ariaLabel: _unref(t)('valueLists.breadcrumbLabel'),
                            "data-testid": "value-lists-breadcrumb"
                        }, null, 8 /* PROPS */, ["items", "ariaLabel"]),
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(t)("valueLists.title")), 1 /* TEXT */),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("valueLists.subtitle")), 1 /* TEXT */)
                    ]),
                    (_unref(store).loadError)
                        ? (_openBlock(), _createBlock(ModuleState, {
                            key: 0,
                            type: "error",
                            "data-testid": "value-lists-error",
                            title: _unref(t)('valueLists.loadError'),
                            "cta-icon": "ri-refresh-line",
                            "cta-label": _unref(t)('common.retry'),
                            onCta: _cache[0] || (_cache[0] = ($event) => (_unref(store).load()))
                        }, null, 8 /* PROPS */, ["title", "cta-label"]))
                        : (_openBlock(), _createBlock(_unref(TableShell), {
                            key: 1,
                            query: _unref(tableQuery),
                            views: _unref(tableViews),
                            labels: tableLabels.value,
                            permissions: { canSaveView: false, canShareView: false },
                            "tab-dropdowns": _unref(tabDropdowns),
                            "column-settings": _unref(columnSettings),
                            "search-placeholder": _unref(t)('valueLists.searchPlaceholder'),
                            loading: _unref(store).isLoading,
                            "show-view-selector": "",
                            bordered: ""
                        }, {
                            filter: _withCtx(() => [
                                _createVNode(_unref(TableFilterSelector), _mergeProps(_unref(filterSelectorLabels), {
                                    fields: filterFields.value,
                                    "active-filters": {},
                                    "trigger-label": _unref(t)('table.filter'),
                                    "trigger-icon-left": "ri-filter-2-line",
                                    "step1-search-placeholder": _unref(t)('filters.searchPlaceholder'),
                                    "confirm-label": _unref(t)('table.tabs.apply'),
                                    "back-label": _unref(t)('common.back'),
                                    "advanced-filters-label": "",
                                    "no-results-label": _unref(t)('common.noResults'),
                                    "data-testid": "value-lists-filter"
                                }), null, 16 /* FULL_PROPS */, ["fields", "trigger-label", "step1-search-placeholder", "confirm-label", "back-label", "no-results-label"])
                            ]),
                            table: _withCtx(() => [
                                _createVNode(_unref(DataTable), {
                                    columns: columns.value,
                                    data: pageRows.value,
                                    "column-visibility": _unref(columnSettings).visibility,
                                    "column-order": _unref(columnSettings).order.value,
                                    "column-pinning": _unref(columnSettings).pinning.value,
                                    "has-settings": "",
                                    "sticky-header": "",
                                    "settings-aria-label": _unref(t)('columnSettings.title'),
                                    "empty-text": _unref(t)(searchTerm.value ? 'valueLists.noSearchResult' : 'valueLists.empty'),
                                    "region-label": _unref(t)('valueLists.title'),
                                    onSettingsClick: _unref(columnSettings).openDrawer,
                                    "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                                    "onUpdate:columnPinning": _unref(columnSettings).setPinning
                                }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "settings-aria-label", "empty-text", "region-label", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning"])
                            ]),
                            pagination: _withCtx(() => [
                                (filteredRows.value.length > 0)
                                    ? (_openBlock(), _createBlock(_unref(Pager), _mergeProps({
                                        key: 0,
                                        "current-page": _unref(tableQuery).state.page,
                                        "total-pages": totalPages.value,
                                        "total-items": filteredRows.value.length,
                                        "items-per-page": _unref(tableQuery).state.pageSize
                                    }, pagerLabels.value, {
                                        "rows-per-page-options": ROW_OPTIONS,
                                        "show-go-to-page": "",
                                        onPageChange: _unref(tableQuery).setPage,
                                        onItemsPerPageChange: _unref(tableQuery).setPageSize
                                    }), null, 16 /* FULL_PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "onPageChange", "onItemsPerPageChange"]))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "column-settings", "search-placeholder", "loading"]))
                ])
            ]));
        };
    }
});
